/**
 * Sparkline — the one inline trend polyline. A tiny SVG that normalizes a
 * numeric series to a 100×`height` viewBox and draws it as a single polyline.
 * Consolidates two byte-for-byte-equivalent copies that lived inside
 * `stat-card.tsx` (`Sparkline`) and `dashboard/insight-card.tsx`
 * (`InsightSpark`). The richer KPI visualization (`hero-kpi.tsx`'s `KpiCurve`
 * — target/pace lines, verdict tone, area fill) stays its own component; it is
 * a different chart, not a sparkline.
 *
 * Inline SVG only — no Recharts on the critical path. `preserveAspectRatio`
 * is "none" so the parent's width/height (via `className`) drives the size.
 */
export interface SparklineProps {
  values: number[];
  /** Stroke color. Default `var(--chart-4)`. */
  color?: string;
  /** viewBox height (width is fixed at 100, scaled by the parent). Default 48. */
  height?: number;
  /** Inner padding in viewBox units. Default 4. */
  padding?: number;
  /** Stroke opacity. Default 0.7. */
  strokeOpacity?: number;
  /** Rendered size class on the `<svg>`. Default "h-12 w-full". */
  className?: string;
  /**
   * Hide from the a11y tree (when the value is announced nearby). When false
   * (default), a `<title>` + `aria-label` summarize the first→last move so the
   * trend is legible as values, not just a shape.
   */
  ariaHidden?: boolean;
}

export function Sparkline({
  values,
  color = "var(--chart-4)",
  height = 48,
  padding = 4,
  strokeOpacity = 0.7,
  className = "h-12 w-full",
  ariaHidden = false,
}: SparklineProps) {
  if (values.length === 0) return null;
  const w = 100;
  const h = height;
  // A single point is not a line — duplicate it so the polyline renders flat
  // instead of as a degenerate dot.
  const data = values.length === 1 ? [values[0]!, values[0]!] : values;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const stepX = data.length > 1 ? (w - padding * 2) / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = padding + i * stepX;
    const y = h - padding - ((v - min) / span) * (h - padding * 2);
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });

  const first = values[0]!;
  const last = values[values.length - 1]!;
  const trendWord = last > first ? "up" : last < first ? "down" : "flat";
  const summary = `${values.length}-point trend: ${first} → ${last} (${trendWord})`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={className}
      {...(ariaHidden
        ? { "aria-hidden": true as const }
        : { role: "img", "aria-label": summary })}
    >
      {ariaHidden ? null : <title>{summary}</title>}
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeOpacity={strokeOpacity}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
